import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import Button from '../../components/common/Button';
import { PlayingBanner } from './playingUi';
import { getActiveRole, getRoles } from '../../utils/auth';
import { resolveClassroomPortalRole } from '../../utils/classroomPortalRole';
import { bookCandidates, lineForEco, listBookOpenings, lookupOpening } from '../../playing/openingBook';
import { localizeOpeningName } from '../../playing/openingLocalize';
import { getChessGame, getMyGames, getStaffUserGames, spectateGame } from '../../api/playing';
import { listAllActiveStudents, listCoachRosterStudents, listManagerActiveStudents } from '../../api/users';
import { buildTaggedPgn, pgnEventLabel, pgnTimeControlTag, pgnTerminationTag } from '../../playing/gameView';
import { mistakePliesFromQualities, serverMovesToClubReview } from '../../playing/gameAnalysis';
import { formatFullName } from '../../utils/name';
import usePrefersReducedMotion from '../../playing/usePrefersReducedMotion';

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const ARROW_COLOR = 'rgb(34, 197, 94)';

function wrapPgn(text) {
  return text.includes('[') ? text : `[Event "?"]\n\n${text}`;
}

/** Build FEN list for every ply in a PGN (start → tip). */
function fensFromPgn(pgnText) {
  const c = new Chess();
  c.loadPgn(wrapPgn(pgnText), { strict: false });
  const verbose = c.history({ verbose: true });
  const headers = c.header();
  const start = headers.FEN && headers.SetUp === '1' ? headers.FEN : START_FEN;
  const replay = new Chess(start);
  const fens = [replay.fen()];
  verbose.forEach((m) => {
    replay.move({ from: m.from, to: m.to, promotion: m.promotion });
    fens.push(replay.fen());
  });
  return fens;
}

function indexOfFen(fens, targetFen) {
  if (!targetFen || !Array.isArray(fens)) return fens.length - 1;
  const tip = new Chess(targetFen).fen();
  const idx = fens.findIndex((f) => f === tip);
  return idx >= 0 ? idx : fens.length - 1;
}

function readInitial() {
  const params = new URLSearchParams(window.location.search);
  const pgnQ = params.get('pgn');
  let storedPgn = '';
  try {
    storedPgn = sessionStorage.getItem('playing.coachBoardPgn') || '';
    if (storedPgn) sessionStorage.removeItem('playing.coachBoardPgn');
  } catch {
    /* ignore */
  }
  const pgnSource = pgnQ ? decodeURIComponent(pgnQ) : storedPgn;
  const fenQ = params.get('fen');
  const arrowsQ = params.get('arrows') || '';
  const hlQ = params.get('hl') || '';
  const parseMarks = () => {
    const arrows = arrowsQ
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s) => /^[a-h][1-8][a-h][1-8]$/.test(s))
      .map((s) => [s.slice(0, 2), s.slice(2, 4), ARROW_COLOR]);
    const highlights = {};
    hlQ.split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s) => /^[a-h][1-8]$/.test(s))
      .forEach((sq) => {
        highlights[sq] = { background: 'rgba(250, 204, 21, 0.55)' };
      });
    return { arrows, highlights };
  };
  const marks = parseMarks();
  const plyQ = params.get('ply');
  const requestedPly = plyQ != null && plyQ !== '' ? Number(plyQ) : NaN;
  if (pgnSource) {
    try {
      const line = fensFromPgn(pgnSource);
      let fen = line[line.length - 1];
      let ply = line.length - 1;
      if (fenQ) {
        try {
          const wanted = new Chess(decodeURIComponent(fenQ)).fen();
          ply = indexOfFen(line, wanted);
          fen = line[ply];
        } catch {
          /* keep tip */
        }
      } else if (Number.isFinite(requestedPly) && line.length > 0) {
        ply = Math.max(0, Math.min(line.length - 1, requestedPly));
        fen = line[ply];
      }
      return { fen, pgn: pgnSource, line, ply, ...marks };
    } catch {
      /* fall through */
    }
  }
  if (!fenQ) return { fen: START_FEN, pgn: '', line: null, ply: 0, ...marks };
  try {
    const fen = new Chess(decodeURIComponent(fenQ)).fen();
    return { fen, pgn: '', line: null, ply: 0, ...marks };
  } catch {
    return { fen: START_FEN, pgn: '', line: null, ply: 0, ...marks };
  }
}

export default function CoachBoardPage() {
  const { t } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = useMemo(() => readInitial(), [searchParams]);
  const [fen, setFen] = useState(initial.fen);
  const [fenDraft, setFenDraft] = useState(initial.fen);
  const [pgnDraft, setPgnDraft] = useState(initial.pgn);
  const [fenError, setFenError] = useState('');
  const [pgnError, setPgnError] = useState('');
  const [notice, setNotice] = useState('');
  const [orientation, setOrientation] = useState('white');
  const [arrowFrom, setArrowFrom] = useState(null);
  const [highlights, setHighlights] = useState(() => initial.highlights || {});
  const [arrows, setArrows] = useState(() => initial.arrows || []);
  const [history, setHistory] = useState([initial.fen]);
  const [lineFens, setLineFens] = useState(initial.line);
  const [viewPly, setViewPly] = useState(initial.ply);
  const [boardWidth, setBoardWidth] = useState(520);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [drillOn, setDrillOn] = useState(false);
  const [drillScore, setDrillScore] = useState({ correct: 0, attempts: 0 });
  const [drillFeedback, setDrillFeedback] = useState('');
  const [loadedGame, setLoadedGame] = useState(null);
  const [clubQualities, setClubQualities] = useState({});
  const [recentGames, setRecentGames] = useState([]);
  const [recentGamesError, setRecentGamesError] = useState('');
  const [rosterStudents, setRosterStudents] = useState([]);
  const [rosterError, setRosterError] = useState('');
  const [fairnessHint, setFairnessHint] = useState('');
  const bookOpenings = useMemo(() => listBookOpenings(32), []);
  const gameIdParam = searchParams.get('gameId');
  const plyParam = searchParams.get('ply');
  const studentIdParam = searchParams.get('studentId');
  const teachingKey = [
    searchParams.get('fen') || '',
    searchParams.get('pgn') || '',
    searchParams.get('ply') || '',
    searchParams.get('arrows') || '',
    searchParams.get('hl') || '',
    gameIdParam || '',
  ].join('|');
  const isStaff = useMemo(() => {
    const roles = getRoles() || [];
    return roles.some((r) => ['ADMIN', 'MANAGER', 'COACH', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_COACH'].includes(String(r).toUpperCase()));
  }, []);

  // Re-apply teaching deep links when query changes while already on this page (SPA nav).
  useEffect(() => {
    if (gameIdParam) return;
    setFen(initial.fen);
    setFenDraft(initial.fen);
    setPgnDraft(initial.pgn);
    setFenError('');
    setPgnError('');
    setHighlights(initial.highlights || {});
    setArrows(initial.arrows || []);
    setLineFens(initial.line);
    setViewPly(initial.ply);
    setHistory([initial.fen]);
    setLoadedGame(null);
    setClubQualities({});
  // teachingKey captures fen/pgn/ply/arrows/hl/gameId; initial values apply for that key.
  }, [teachingKey, gameIdParam, initial]);

  useEffect(() => {
    if (!isStaff) return undefined;
    let cancelled = false;
    const role = String(getActiveRole() || '').toUpperCase();
    const load = role === 'COACH'
      ? listCoachRosterStudents()
      : role === 'MANAGER'
        ? listManagerActiveStudents()
        : listAllActiveStudents();
    load
      .then((rows) => {
        if (cancelled) return;
        setRosterStudents(Array.isArray(rows) ? rows : []);
        setRosterError('');
      })
      .catch(() => {
        if (!cancelled) {
          setRosterStudents([]);
          setRosterError(t('playing.rosterLoadFailed', {
            defaultValue: 'Could not load coach roster for the picker.',
          }));
        }
      });
    return () => { cancelled = true; };
  }, [isStaff, t]);

  useEffect(() => {
    let cancelled = false;
    const load = studentIdParam && isStaff
      ? getStaffUserGames(studentIdParam, 0, 20)
      : getMyGames(0, 20);
    load
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : (data?.content || data?.games || []);
        const finished = (rows || []).filter((g) => {
          const st = String(g?.status || '').toUpperCase();
          return ['WHITE_WON', 'BLACK_WON', 'DRAW'].includes(st) || g?.endedAt;
        }).slice(0, 12);
        setRecentGames(finished);
        setRecentGamesError('');
      })
      .catch(() => {
        if (!cancelled) {
          setRecentGames([]);
          setRecentGamesError(t('playing.recentGamesLoadFailed', {
            defaultValue: 'Could not load recent games for the picker.',
          }));
        }
      });
    return () => { cancelled = true; };
  }, [t, studentIdParam, isStaff]);

  useEffect(() => {
    if (!gameIdParam) return undefined;
    let cancelled = false;
    const applyLoadedGame = (g) => {
      if (cancelled || !g) return;
      const ucis = (g.moves || []).map((m) => m.uci).filter(Boolean);
      const open = ucis.length ? lookupOpening(ucis) : null;
      const eventName = pgnEventLabel(g.gameType, !!g.rated);
      const incSec = g.incrementMs != null ? Math.round(Number(g.incrementMs) / 1000) : 0;
      const pgnOpts = {
        moveText: g.pgn || '',
        moves: g.moves,
        whiteName: g.whiteName || '?',
        blackName: g.blackName || '?',
        status: g.status,
        event: eventName,
        site: 'ChessDesk',
        round: '?',
        dateIso: g.endedAt || g.startedAt || g.createdAt,
        eco: open?.eco,
        opening: open?.name,
        timeControl: pgnTimeControlTag(g.timeControlSeconds, incSec),
        whiteElo: g.whiteRatingBefore,
        blackElo: g.blackRatingBefore,
        termination: pgnTerminationTag(g.resultReason),
      };
      const pgn = (g.pgn && String(g.pgn).trim())
        || buildTaggedPgn({ ...pgnOpts, includeAnnotations: false });
      const studyPgn = buildTaggedPgn({ ...pgnOpts, includeAnnotations: true }) || pgn;
      try {
        const line = fensFromPgn(studyPgn || pgn);
        const review = serverMovesToClubReview(g.moves);
        const qualities = review?.qualities || {};
        setClubQualities(qualities);
        setLoadedGame({ ...g, studyPgn });
        const flagMoves = (g.moves || []).filter((m) => {
          const f = String(m?.engineFlag || '').toUpperCase();
          return f === 'WATCH' || f === 'SUSPECT' || f === 'CRITICAL';
        });
        if (flagMoves.length > 0) {
          const top = flagMoves.reduce((a, b) => {
            const order = { CRITICAL: 3, SUSPECT: 2, WATCH: 1 };
            return (order[String(b.engineFlag || '').toUpperCase()] || 0)
              >= (order[String(a.engineFlag || '').toUpperCase()] || 0) ? b : a;
          }, flagMoves[0]);
          setFairnessHint(t('playing.fairnessTeachingHint', {
            defaultValue: '{{n}} moves flagged (highest: {{level}}). Use for teaching — open fair-play if needed.',
            n: flagMoves.length,
            level: String(top.engineFlag || '').toLowerCase(),
          }));
        } else {
          setFairnessHint('');
        }
        const requested = plyParam != null ? Number(plyParam) : NaN;
        const startPly = Number.isFinite(requested)
          ? Math.max(0, Math.min(line.length - 1, requested))
          : line.length - 1;
        setLineFens(line);
        setViewPly(startPly);
        setHistory([line[startPly]]);
        setFen(line[startPly]);
        setFenDraft(line[startPly]);
        setPgnDraft(studyPgn || pgn);
        setFenError('');
        setPgnError('');
        setNotice(t('playing.gameLoadedOnCoachBoard', {
          defaultValue: 'Loaded game #{{id}}',
          id: g.id,
        }));
        setTimeout(() => setNotice(''), 2500);
      } catch {
        if (g.currentFen || g.initialFen) {
          const f = g.currentFen || g.initialFen;
          setFen(f);
          setFenDraft(f);
          setHistory([f]);
          setLineFens(null);
        }
        setPgnError(t('playing.gameLoadFailed', {
          defaultValue: 'Could not load that game onto the coach board.',
        }));
      }
    };

    getChessGame(gameIdParam)
      .then((g) => applyLoadedGame(g))
      .catch(() => spectateGame(gameIdParam)
        .then((g) => applyLoadedGame(g))
        .catch(() => {
          if (!cancelled) {
            setPgnError(t('playing.gameLoadFailed', {
              defaultValue: 'Could not load that game onto the coach board.',
            }));
          }
        }));
    return () => { cancelled = true; };
  }, [gameIdParam, plyParam, studentIdParam, t]);

  const mistakePlies = useMemo(
    () => mistakePliesFromQualities(clubQualities),
    [clubQualities],
  );

  const drillUciHistory = useMemo(() => {
    const fens = lineFens && lineFens.length > 1
      ? lineFens.slice(0, viewPly + 1)
      : history;
    if (!Array.isArray(fens) || fens.length <= 1) return [];
    try {
      const out = [];
      for (let i = 1; i < fens.length; i += 1) {
        const before = fens[i - 1];
        const after = fens[i];
        const c = new Chess(before);
        const mv = c.moves({ verbose: true }).find((m) => {
          const trial = new Chess(before);
          trial.move({ from: m.from, to: m.to, promotion: m.promotion });
          return trial.fen() === after;
        });
        if (!mv) break;
        out.push(`${mv.from}${mv.to}${mv.promotion || ''}`);
      }
      return out;
    } catch {
      return [];
    }
  }, [lineFens, viewPly, history]);

  const drillCandidates = useMemo(
    () => (drillOn ? bookCandidates(drillUciHistory).slice(0, 5) : []),
    [drillOn, drillUciHistory],
  );

  useEffect(() => {
    const onResize = () => {
      const w = Math.min(560, Math.max(280, window.innerWidth - 48));
      setBoardWidth(w);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const classroomHref = useMemo(() => {
    const role = resolveClassroomPortalRole(null, null, getActiveRole());
    if (!role) return '/playing';
    return `/${String(role).toLowerCase()}/classroom/live`;
  }, []);

  const showFen = (nextFen) => {
    setFen(nextFen);
    setFenDraft(nextFen);
  };

  const pushFen = (nextFen) => {
    showFen(nextFen);
    setLineFens(null);
    setHistory((prev) => {
      if (prev[prev.length - 1] === nextFen) return prev;
      return [...prev, nextFen];
    });
  };

  const clearMarks = () => {
    setHighlights({});
    setArrows([]);
    setArrowFrom(null);
  };

  const applyFen = () => {
    try {
      const c = new Chess(fenDraft.trim());
      pushFen(c.fen());
      setFenError('');
      setPgnError('');
      clearMarks();
    } catch {
      setFenError(t('playing.invalidFen', { defaultValue: 'Invalid FEN' }));
    }
  };

  const applyPgn = () => {
    try {
      const text = pgnDraft.trim();
      if (!text) {
        setPgnError(t('playing.invalidPgn', { defaultValue: 'Paste a PGN first' }));
        return;
      }
      const line = fensFromPgn(text);
      const tip = line[line.length - 1];
      setLineFens(line);
      setViewPly(line.length - 1);
      setHistory([tip]);
      showFen(tip);
      setFenError('');
      setPgnError('');
      clearMarks();
      setNotice(t('playing.pgnLoaded', { defaultValue: 'PGN loaded — use Prev/Next to scrub plies' }));
      setTimeout(() => setNotice(''), 2500);
    } catch {
      setPgnError(t('playing.invalidPgn', { defaultValue: 'Could not parse PGN' }));
    }
  };

  const loadBookOpening = (eco) => {
    if (!eco) return;
    const line = lineForEco(eco);
    if (!line?.ucis?.length) {
      setPgnError(t('playing.openingLoadFailed', { defaultValue: 'Could not load that opening' }));
      return;
    }
    try {
      const c = new Chess();
      line.ucis.forEach((uci) => {
        const u = String(uci).toLowerCase();
        c.move({
          from: u.slice(0, 2),
          to: u.slice(2, 4),
          promotion: u.length > 4 ? u[4] : undefined,
        });
      });
      const pgn = c.pgn({ maxWidth: 0, newline: ' ' });
      setPgnDraft(pgn);
      const fens = fensFromPgn(pgn);
      const tip = fens[fens.length - 1];
      setLineFens(fens);
      setViewPly(fens.length - 1);
      setHistory([tip]);
      showFen(tip);
      setFenError('');
      setPgnError('');
      clearMarks();
      setNotice(t('playing.openingLoaded', {
        defaultValue: 'Loaded {{name}}',
        name: line.name || eco,
      }));
      setTimeout(() => setNotice(''), 2500);
    } catch {
      setPgnError(t('playing.openingLoadFailed', { defaultValue: 'Could not load that opening' }));
    }
  };

  const copyFen = async () => {
    try {
      await navigator.clipboard.writeText(fen);
      setNotice(t('playing.fenCopied', { defaultValue: 'FEN copied' }));
      setTimeout(() => setNotice(''), 2000);
    } catch {
      setFenError(t('playing.copyFailed', { defaultValue: 'Could not copy' }));
    }
  };

  const copyTeachingLink = async () => {
    try {
      const params = new URLSearchParams();
      params.set('fen', fen);
      const arrowStr = (arrows || [])
        .map((a) => (Array.isArray(a) && a[0] && a[1] ? `${a[0]}${a[1]}` : ''))
        .filter(Boolean)
        .join(',');
      if (arrowStr) params.set('arrows', arrowStr);
      const hl = Object.keys(highlights || {}).join(',');
      if (hl) params.set('hl', hl);
      const url = `${window.location.origin}/playing/coach-board?${params.toString()}`;
      await navigator.clipboard.writeText(url);
      setNotice(t('playing.teachingLinkCopied', { defaultValue: 'Teaching link copied' }));
      setTimeout(() => setNotice(''), 2000);
    } catch {
      setFenError(t('playing.copyFailed', { defaultValue: 'Could not copy' }));
    }
  };

  const goPrevPly = useCallback(() => {
    if (lineFens && lineFens.length > 1) {
      setViewPly((i) => {
        const next = Math.max(0, i - 1);
        showFen(lineFens[next]);
        clearMarks();
        return next;
      });
      return;
    }
    setHistory((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.slice(0, -1);
      showFen(next[next.length - 1]);
      clearMarks();
      return next;
    });
  }, [lineFens]);

  const goNextPly = useCallback(() => {
    if (!lineFens || lineFens.length <= 1) return;
    setViewPly((i) => {
      const next = Math.min(lineFens.length - 1, i + 1);
      showFen(lineFens[next]);
      clearMarks();
      return next;
    });
  }, [lineFens]);

  const goToPly = useCallback((ply) => {
    if (!lineFens?.length) return;
    const next = Math.max(0, Math.min(lineFens.length - 1, ply));
    setViewPly(next);
    showFen(lineFens[next]);
    clearMarks();
  }, [lineFens]);

  const jumpMistake = useCallback((dir) => {
    if (!mistakePlies.length || !lineFens?.length) return;
    const cur = viewPly;
    if (dir > 0) {
      const next = mistakePlies.find((p) => p > cur) ?? mistakePlies[0];
      goToPly(next);
      return;
    }
    const prev = [...mistakePlies].reverse().find((p) => p < cur) ?? mistakePlies[mistakePlies.length - 1];
    goToPly(prev);
  }, [mistakePlies, viewPly, lineFens, goToPly]);

  const copyStudyPgn = async () => {
    const text = loadedGame?.studyPgn || pgnDraft;
    if (!text?.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      setNotice(t('playing.studyPgnCopied', { defaultValue: 'Annotated PGN copied' }));
      setTimeout(() => setNotice(''), 2000);
    } catch {
      setPgnError(t('playing.copyFailed', { defaultValue: 'Could not copy' }));
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT') return;
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setShortcutHelpOpen((o) => !o);
        return;
      }
      if (e.key === 'Escape' && shortcutHelpOpen) {
        setShortcutHelpOpen(false);
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrevPly();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNextPly();
      } else if (e.key === 'Home' && lineFens?.length) {
        e.preventDefault();
        setViewPly(0);
        showFen(lineFens[0]);
        clearMarks();
      } else if (e.key === 'End' && lineFens?.length) {
        e.preventDefault();
        const tip = lineFens.length - 1;
        setViewPly(tip);
        showFen(lineFens[tip]);
        clearMarks();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrevPly, goNextPly, lineFens, shortcutHelpOpen]);

  const onSquareRightClick = (square) => {
    if (arrowFrom && arrowFrom !== square) {
      setArrows((prev) => {
        const withoutDup = prev.filter(
          ([from, to]) => !(from === arrowFrom && to === square),
        );
        return [...withoutDup, [arrowFrom, square, ARROW_COLOR]];
      });
      setArrowFrom(null);
      return;
    }
    setArrowFrom(square);
    setHighlights((prev) => {
      const next = { ...prev };
      if (next[square]) {
        delete next[square];
        setArrowFrom(null);
        return next;
      }
      next[square] = { background: 'rgba(250, 204, 21, 0.55)' };
      return next;
    });
  };

  const onDrop = (from, to) => {
    try {
      const c = new Chess(fen);
      const mv = c.move({ from, to, promotion: 'q' });
      if (!mv) return false;
      if (drillOn) {
        const uci = `${from}${to}${mv.promotion || ''}`;
        if (drillCandidates.length > 0) {
          const ok = drillCandidates.some((x) => String(x.uci).toLowerCase() === uci.toLowerCase());
          setDrillScore((s) => ({
            correct: s.correct + (ok ? 1 : 0),
            attempts: s.attempts + 1,
          }));
          setDrillFeedback(ok
            ? t('playing.drillCorrect', { defaultValue: 'Book move!' })
            : t('playing.drillWrong', {
              defaultValue: 'Not in the club book{{hint}}',
              hint: drillCandidates[0]?.uci ? ` — try ${drillCandidates[0].uci}` : '',
            }));
          setTimeout(() => setDrillFeedback(''), 2200);
          if (!ok) return false;
        }
      }
      const nextFen = c.fen();
      if (lineFens) {
        const truncated = lineFens.slice(0, viewPly + 1);
        truncated.push(nextFen);
        setLineFens(truncated);
        setViewPly(truncated.length - 1);
        showFen(nextFen);
      } else {
        pushFen(nextFen);
      }
      return true;
    } catch {
      return false;
    }
  };

  const canPrev = lineFens ? viewPly > 0 : history.length > 1;
  const canNext = !!(lineFens && viewPly < lineFens.length - 1);
  const plyLabel = lineFens
    ? t('playing.coachPlyOf', {
      defaultValue: 'Ply {{current}} / {{total}}',
      current: viewPly,
      total: Math.max(0, lineFens.length - 1),
    })
    : null;

  return (
    <div className="page-shell-form playing-page max-w-3xl">
      <PageHeaderSection
        eyebrow={t('nav.playingPlatform')}
        title={t('playing.coachBoard', { defaultValue: 'Coach board' })}
        subtitle={t('playing.coachBoardSubtitle', {
          defaultValue: 'Shared teaching board — set a position, draw arrows, open Classroom for live A/V',
        })}
      />

      <PlayingBanner tone="warning">
        {t('playing.coachBoardClassroomHint', {
          defaultValue: 'For voice/video with students, use Classroom live. This board is for positions and arrows only.',
        })}
        {' '}
        <Link to={classroomHref} className="font-semibold underline">
          {t('playing.openClassroomLive', { defaultValue: 'Open Classroom live' })}
        </Link>
      </PlayingBanner>

      {notice && <PlayingBanner tone="success">{notice}</PlayingBanner>}

      {shortcutHelpOpen && (
        <div className="list-table-card mb-4 p-4 text-sm text-slate-700" role="dialog" aria-label={t('playing.shortcutHelpTitle', { defaultValue: 'Keyboard shortcuts' })}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="font-semibold text-slate-900">
              {t('playing.shortcutHelpTitle', { defaultValue: 'Keyboard shortcuts' })}
            </p>
            <Button variant="page-secondary" onClick={() => setShortcutHelpOpen(false)}>
              {t('playing.dismissResult')}
            </Button>
          </div>
          <ul className="grid gap-1 text-xs sm:grid-cols-2">
            <li><kbd className="font-mono">← →</kbd> {t('playing.shortcutScrub', { defaultValue: 'Step through moves' })}</li>
            <li><kbd className="font-mono">Home / End</kbd> {t('playing.shortcutEnds', { defaultValue: 'First / last position' })}</li>
            <li><kbd className="font-mono">?</kbd> / Esc {t('playing.shortcutToggleHelp', { defaultValue: 'Toggle this help' })}</li>
          </ul>
        </div>
      )}

      <div className="list-table-card space-y-4 p-4">
        {isStaff && (
          <label className="block text-sm text-slate-700">
            {t('playing.coachRosterPicker', { defaultValue: 'Student on roster' })}
            <select
              className="select mt-1 w-full"
              value={studentIdParam || ''}
              onChange={(e) => {
                const id = e.target.value;
                const next = new URLSearchParams(searchParams);
                if (!id) next.delete('studentId');
                else next.set('studentId', id);
                next.delete('gameId');
                next.delete('ply');
                setSearchParams(next, { replace: true });
              }}
            >
              <option value="">
                {t('playing.coachRosterPickerMe', { defaultValue: 'My own games' })}
              </option>
              {rosterStudents.map((s) => {
                const id = s.id ?? s.userId ?? s.user_id;
                const name = formatFullName(s.firstName, s.middleName, s.lastName)
                  || s.fullName || s.displayName || s.username || `#${id}`;
                return (
                  <option key={id} value={id}>{name}</option>
                );
              })}
            </select>
          </label>
        )}
        {rosterError && <PlayingBanner tone="warning">{rosterError}</PlayingBanner>}
        {fairnessHint && (
          <PlayingBanner tone="warning">
            <p>{fairnessHint}</p>
            {loadedGame?.id && isStaff && (
              <div className="mt-2">
                <Link
                  to={`/playing/fair-play/${loadedGame.id}`}
                  className="font-semibold underline"
                >
                  {t('playing.openFairPlay', { defaultValue: 'Open fair-play review' })}
                </Link>
              </div>
            )}
          </PlayingBanner>
        )}
        <label className="block text-sm text-slate-700">
          {t('playing.loadRecentGame', { defaultValue: 'Load a recent game' })}
          <select
            className="select mt-1 w-full"
            value={gameIdParam || ''}
            onChange={(e) => {
              const id = e.target.value;
              if (!id) {
                const next = new URLSearchParams(searchParams);
                next.delete('gameId');
                next.delete('ply');
                setSearchParams(next, { replace: true });
                return;
              }
              const next = new URLSearchParams(searchParams);
              next.set('gameId', id);
              next.delete('ply');
              setSearchParams(next, { replace: true });
            }}
          >
            <option value="">
              {t('playing.loadRecentGamePlaceholder', { defaultValue: 'Choose a finished game…' })}
            </option>
            {recentGames.map((g) => (
              <option key={g.id} value={g.id}>
                {g.whiteName || '…'}
                {' vs '}
                {g.blackName || '…'}
                {g.id ? ` (#${g.id})` : ''}
              </option>
            ))}
          </select>
          {!recentGamesError && recentGames.length === 0 && (
            <p className="mt-1 text-xs text-slate-500">
              {t('playing.noRecentGamesToLoad', {
                defaultValue: 'No finished games to load yet. Paste a PGN or set a FEN below.',
              })}
            </p>
          )}
        </label>
        {recentGamesError && <PlayingBanner tone="warning">{recentGamesError}</PlayingBanner>}
        <label className="block text-sm text-slate-700">
          {t('playing.pickOpening', { defaultValue: 'Load club opening' })}
          <select
            className="select mt-1 w-full"
            defaultValue=""
            onChange={(e) => {
              const eco = e.target.value;
              e.target.value = '';
              if (eco) loadBookOpening(eco);
            }}
          >
            <option value="">
              {t('playing.pickOpeningPlaceholder', { defaultValue: 'Choose an opening…' })}
            </option>
            {bookOpenings.map((o) => (
              <option key={o.eco} value={o.eco}>
                {o.eco}
                {' — '}
                {localizeOpeningName(o.name, t)}
              </option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={drillOn ? 'page-primary' : 'page-secondary'}
            onClick={() => {
              setDrillOn((v) => !v);
              setDrillFeedback('');
              if (!drillOn) setDrillScore({ correct: 0, attempts: 0 });
            }}
          >
            {drillOn
              ? t('playing.drillOn', { defaultValue: 'Book drill on' })
              : t('playing.drillOff', { defaultValue: 'Book drill' })}
          </Button>
          {drillOn && (
            <span className="text-sm text-slate-600">
              {t('playing.drillScore', {
                defaultValue: '{{correct}} / {{attempts}} book moves',
                correct: drillScore.correct,
                attempts: drillScore.attempts,
              })}
              {drillCandidates.length > 0
                ? ` · ${drillCandidates.map((c) => c.uci).slice(0, 3).join(', ')}`
                : ` · ${t('playing.drillOutOfBook', { defaultValue: 'Out of book' })}`}
            </span>
          )}
        </div>
        {drillFeedback && (
          <p className="text-sm font-medium text-slate-800" role="status">{drillFeedback}</p>
        )}
        <div className="playing-board-frame mx-auto w-full max-w-[560px]">
          <Chessboard
            position={fen}
            onPieceDrop={onDrop}
            onSquareRightClick={onSquareRightClick}
            boardOrientation={orientation}
            boardWidth={boardWidth}
            customSquareStyles={highlights}
            customArrows={arrows}
            arePiecesDraggable
            animationDuration={reducedMotion ? 0 : 180}
            showBoardNotation
          />
        </div>
        <p className="text-center text-xs text-slate-500">
          {t('playing.coachBoardArrowHint', {
            defaultValue: 'Right-click a square to highlight; right-click another square to draw an arrow.',
          })}
        </p>
        {plyLabel && (
          <p className="text-center text-xs font-medium tabular-nums text-slate-600">
            {plyLabel}
            {clubQualities[viewPly]
              ? ` · ${t(`playing.quality.${clubQualities[viewPly]}`, {
                defaultValue: clubQualities[viewPly],
              })}`
              : ''}
          </p>
        )}
        {mistakePlies.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button variant="page-secondary" onClick={() => jumpMistake(-1)}>
              {t('playing.prevMistake', { defaultValue: 'Prev mistake' })}
            </Button>
            <span className="text-xs text-slate-500">
              {t('playing.mistakeCount', {
                defaultValue: '{{n}} mistakes',
                n: mistakePlies.length,
              })}
            </span>
            <Button variant="page-secondary" onClick={() => jumpMistake(1)}>
              {t('playing.nextMistake', { defaultValue: 'Next mistake' })}
            </Button>
          </div>
        )}

        <label className="block text-sm text-slate-700">
          FEN
          <textarea
            className="input mt-1 font-mono text-xs"
            rows={2}
            value={fenDraft}
            onChange={(e) => setFenDraft(e.target.value)}
          />
        </label>
        {fenError && <PlayingBanner tone="danger">{fenError}</PlayingBanner>}

        <label className="block text-sm text-slate-700">
          {t('playing.pgnPaste', { defaultValue: 'Paste PGN' })}
          <textarea
            className="input mt-1 font-mono text-xs"
            rows={3}
            value={pgnDraft}
            onChange={(e) => setPgnDraft(e.target.value)}
            placeholder={t('playing.pgnPasteHint', { defaultValue: 'Full PGN or move list…' })}
          />
        </label>
        {pgnError && <PlayingBanner tone="danger">{pgnError}</PlayingBanner>}

        <div className="flex flex-wrap gap-2">
          <Button variant="page-primary" onClick={applyFen}>
            {t('playing.applyFen', { defaultValue: 'Apply FEN' })}
          </Button>
          <Button variant="page-secondary" onClick={applyPgn}>
            {t('playing.applyPgn', { defaultValue: 'Load PGN' })}
          </Button>
          <Button variant="page-secondary" onClick={copyFen}>
            {t('playing.copyFen', { defaultValue: 'Copy FEN' })}
          </Button>
          {loadedGame?.studyPgn && (
            <Button variant="page-secondary" onClick={copyStudyPgn}>
              {t('playing.copyStudyPgn', { defaultValue: 'Copy annotated PGN' })}
            </Button>
          )}
          <Button variant="page-secondary" onClick={copyTeachingLink}>
            {t('playing.copyTeachingLink', { defaultValue: 'Copy teaching link' })}
          </Button>
          <Button variant="page-secondary" disabled={!canPrev} onClick={goPrevPly}>
            {t('playing.prevPly', { defaultValue: 'Prev ply' })}
          </Button>
          <Button variant="page-secondary" disabled={!canNext} onClick={goNextPly}>
            {t('playing.nextPly', { defaultValue: 'Next ply' })}
          </Button>
          <Button variant="page-secondary" onClick={() => setShortcutHelpOpen((o) => !o)}>
            {t('playing.shortcuts', { defaultValue: 'Shortcuts' })}
          </Button>
          <Button
            variant="page-secondary"
            onClick={() => {
              pushFen(START_FEN);
              setHistory([START_FEN]);
              setLineFens(null);
              setViewPly(0);
              clearMarks();
              setFenError('');
              setPgnError('');
            }}
          >
            {t('playing.resetBoard', { defaultValue: 'Reset' })}
          </Button>
          <Button
            variant="page-secondary"
            onClick={() => setOrientation((o) => (o === 'white' ? 'black' : 'white'))}
          >
            {t('playing.flipBoard')}
          </Button>
          <Button variant="page-secondary" onClick={clearMarks}>
            {t('playing.clearHighlights', { defaultValue: 'Clear highlights' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
